import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik/dist/types';
import { useCallback } from 'react';
import { useHistory } from 'react-router';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { getResetPasswordDetails } from '@app/redux/auth/auth.selectors';
import { useAppSelector } from '@app/redux/hooks';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { ResetPasswordFormValues } from '@components/forms/resetPassword/interfaces';

function useResetPasswordForm() {
	const history = useHistory();
	const generateRoutePath = useGenerateRoutePath();

	const { startRequest } = useTriggeredRequest(AccountClientRequests.Auth.resetPassword);

	const resetPasswordDetails = useAppSelector(getResetPasswordDetails);

	const initialValues: ResetPasswordFormValues = {
		password: '',
		password_confirmation: '',
		code: '',
		resetPasswordError: '',
		resendCodeError: '',
	};

	const onSubmit = useCallback(
		async (values: ResetPasswordFormValues, formikHelpers: FormikHelpers<ResetPasswordFormValues>) => {
			if (
				!resetPasswordDetails?.auth_form_slug ||
				!resetPasswordDetails?.contact ||
				!resetPasswordDetails?.reset_type
			) {
				return;
			}

			try {
				await startRequest({
					code: values.code,
					new_password: values.password,
					auth_form_slug: resetPasswordDetails.auth_form_slug,
					contact: resetPasswordDetails.contact,
					reset_type: resetPasswordDetails.reset_type,
				});

				history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
			} catch (e) {
				const { response } = e as AxiosError<{
					errors?: {
						description?: string;
						field?: string;
					}[];
				}>;

				const fallbackMessage = 'Oops. Something went wrong.';

				const errorDescription = response?.data?.errors?.[0]?.description || fallbackMessage;

				formikHelpers.setErrors({
					[response?.data?.errors?.[0]?.field || 'resetPasswordError']: errorDescription,
				});
			}
		},
		[resetPasswordDetails, startRequest, history, generateRoutePath],
	);

	return {
		initialValues,
		onSubmit,
	};
}

export default useResetPasswordForm;
